import { useRef, useEffect, useState, useMemo } from "react";
import * as d3 from "d3";
import type { VisualSeed, VisualNode } from "@/hooks/useMapVisualData";
import { ROLE_COLORS } from "./constants";
import { RoleLegend } from "./RoleLegend";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, RotateCcw } from "lucide-react";

interface SunburstChartProps {
  mapName: string;
  seeds: VisualSeed[];
  nodes: VisualNode[];
  filteredNodeIds?: Set<string> | null;
  onNodeClick?: (nodeId: string) => void;
}

interface HierarchyNode {
  name: string;
  role?: string;
  nodeId?: string;
  entity?: string;
  stage?: string;
  seedId?: string;
  seedColor?: string;
  children?: HierarchyNode[];
}

/**
 * SunburstChart v2 — zoomable, color-per-seed, animowane zoom transitions.
 *
 * Features:
 *  - Klik w segment seedu → zoom in (smooth 0.5s transition)
 *  - Klik w środek → zoom out (back to root)
 *  - Klik w node (outer ring) → otwiera detail (onNodeClick callback)
 *  - Każdy seed dostaje własny kolor (D3 categorical scheme)
 *  - Outer ring (nodes) zachowuje role colors
 *  - Hover: highlight + tooltip z entity/stage
 *  - Breadcrumbs trail u góry
 *  - Responsywny rozmiar (auto-fit do containera, min 600px)
 */
export function SunburstChart({
  mapName,
  seeds,
  nodes,
  filteredNodeIds,
  onNodeClick,
}: SunburstChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; lines: string[] } | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>([]);
  const [containerWidth, setContainerWidth] = useState(720);

  // Color palette per seed (D3 Tableau 10 + interpolation for >10 seeds)
  const seedColorScale = useMemo(() => {
    return d3.scaleOrdinal<string, string>()
      .domain(seeds.map((s) => s.id))
      .range(d3.schemeTableau10);
  }, [seeds]);

  // Resize observer
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setContainerWidth(Math.max(600, Math.min(w, 900)));
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!svgRef.current || seeds.length === 0) return;

    const width = containerWidth;
    const height = width;
    const radius = width / 2;

    // Build hierarchy with seed color attribution
    const root: HierarchyNode = {
      name: mapName,
      children: seeds.map((seed) => {
        const color = seedColorScale(seed.id);
        return {
          name: seed.name,
          seedId: seed.id,
          seedColor: color,
          children: nodes
            .filter((n) => n.seed_id === seed.id)
            .filter((n) => !filteredNodeIds || filteredNodeIds.has(n.id))
            .map((n) => ({
              name: n.title,
              role: n.document_role,
              nodeId: n.id,
              entity: n.primary_entity_raw,
              stage: n.engine_stage || "pending",
              seedId: seed.id,
              seedColor: color,
            })),
        };
      }),
    };

    const hierarchy = d3
      .hierarchy(root)
      .sum(() => 1)
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    const partition = d3.partition<HierarchyNode>().size([2 * Math.PI, hierarchy.height + 1]);
    partition(hierarchy);

    type ArcNode = d3.HierarchyRectangularNode<HierarchyNode> & {
      current?: { x0: number; x1: number; y0: number; y1: number };
      target?: { x0: number; x1: number; y0: number; y1: number };
    };

    const allNodes = hierarchy.descendants() as ArcNode[];
    allNodes.forEach((d) => {
      d.current = { x0: d.x0, x1: d.x1, y0: d.y0, y1: d.y1 };
    });

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg
      .attr("viewBox", `${-radius} ${-radius} ${width} ${height}`)
      .style("width", "100%")
      .style("height", "auto");

    const g = svg.append("g");

    const arc = d3
      .arc<ArcNode>()
      .startAngle((d) => d.current!.x0)
      .endAngle((d) => d.current!.x1)
      .padAngle((d) => Math.min((d.current!.x1 - d.current!.x0) / 2, 0.005))
      .padRadius(radius * 0.4)
      .innerRadius((d) => d.current!.y0 * (radius / 3))
      .outerRadius((d) => Math.max(d.current!.y0 * (radius / 3), d.current!.y1 * (radius / 3) - 1));

    function getColor(d: ArcNode): string {
      if (d.depth === 0) return "transparent";
      if (d.depth === 1) return d.data.seedColor || "#6b7280";
      // Outer ring: blend role color with seed color for visual cohesion
      const roleColor = ROLE_COLORS[d.data.role || ""] || "#6b7280";
      return roleColor;
    }

    function arcVisible(d: { x0: number; x1: number; y0: number; y1: number }): boolean {
      return d.y1 <= 3 && d.y0 >= 1 && d.x1 > d.x0;
    }

    function labelVisible(
      d: { x0: number; x1: number; y0: number; y1: number },
      depth: number,
    ): boolean {
      if (d.y1 > 3 || d.y0 < 1 || d.x1 <= d.x0) return false;
      // Wyższy threshold dla outer ring (depth 2) — w zoom-out 180 etykiet to chaos.
      // Po zoom-in segmenty puchną >5×, więc dalej będą widoczne dla wybranego seedu.
      const minArea = depth === 1 ? 0.04 : 0.08;
      return (d.y1 - d.y0) * (d.x1 - d.x0) > minArea;
    }

    function labelTransform(d: { x0: number; x1: number; y0: number; y1: number }): string {
      const x = (((d.x0 + d.x1) / 2) * 180) / Math.PI;
      const y = ((d.y0 + d.y1) / 2) * (radius / 3);
      return `rotate(${x - 90}) translate(${y},0) rotate(${x < 180 ? 0 : 180})`;
    }

    // Draw arcs
    const path = g
      .append("g")
      .selectAll("path")
      .data(allNodes.filter((d) => d.depth > 0))
      .join("path")
      .attr("fill", (d) => getColor(d))
      .attr("fill-opacity", (d) =>
        arcVisible(d.current!) ? (d.depth === 1 ? 0.85 : 0.92) : 0,
      )
      .attr("stroke", "rgba(0,0,0,0.4)")
      .attr("stroke-width", 0.5)
      .attr("d", (d) => arc(d) || "")
      .style("cursor", (d) => (d.children || d.data.nodeId ? "pointer" : "default"));

    // Draw labels
    const label = g
      .append("g")
      .attr("pointer-events", "none")
      .attr("text-anchor", "middle")
      .style("user-select", "none")
      .selectAll("text")
      .data(allNodes.filter((d) => d.depth > 0))
      .join("text")
      .attr("dy", "0.35em")
      .attr("fill-opacity", (d) => +labelVisible(d.current!, d.depth))
      .attr("transform", (d) => labelTransform(d.current!))
      .attr("fill", "#f5f5f7")
      .attr("font-size", (d) => (d.depth === 1 ? "12px" : "9px"))
      .attr("font-weight", (d) => (d.depth === 1 ? "600" : "400"))
      .attr("paint-order", "stroke")
      .attr("stroke", "rgba(0,0,0,0.7)")
      .attr("stroke-width", 2.5)
      .text((d) => {
        // Krótkie podpisy — outer ring 12 znaków, inner 16 (zoom-in nie miesza)
        const max = d.depth === 1 ? 16 : 12;
        return d.data.name.length > max ? d.data.name.slice(0, max - 1) + "…" : d.data.name;
      });

    // Center clickable circle (zoom out)
    const parent = g
      .append("circle")
      .datum(hierarchy as ArcNode)
      .attr("r", radius / 3 - 4)
      .attr("fill", "transparent")
      .attr("pointer-events", "all")
      .style("cursor", "pointer");

    // Center labels
    const centerLabel = g
      .append("g")
      .attr("text-anchor", "middle")
      .attr("pointer-events", "none");

    centerLabel
      .append("text")
      .attr("y", -10)
      .attr("fill", "#a78bfa")
      .attr("font-size", "16px")
      .attr("font-weight", "700")
      .text(mapName.length > 22 ? mapName.slice(0, 19) + "…" : mapName);

    const countText = centerLabel
      .append("text")
      .attr("y", 12)
      .attr("fill", "#9ca3af")
      .attr("font-size", "11px")
      .text(`${seeds.length} seeds · ${nodes.length} nodes`);

    const hintText = centerLabel
      .append("text")
      .attr("y", 28)
      .attr("fill", "#6b7280")
      .attr("font-size", "9px")
      .style("font-style", "italic")
      .text("Klik w segment → zoom");

    // Hover handlers
    path
      .on("mouseover", function (event, d) {
        d3.select(this).attr("fill-opacity", 1).attr("stroke", "#a78bfa").attr("stroke-width", 2);

        // Highlight ancestors
        const ancestors = d.ancestors().map((a) => a as ArcNode);
        path.attr("fill-opacity", (n) => {
          if (!arcVisible(n.current!)) return 0;
          if (ancestors.includes(n)) return 1;
          return 0.4;
        });

        const lines: string[] = [];
        if (d.depth === 1) {
          lines.push(d.data.name);
          lines.push(`${d.children?.length || 0} nodes`);
        } else if (d.depth === 2) {
          lines.push(d.data.name);
          if (d.data.role) lines.push(`Rola: ${d.data.role}`);
          if (d.data.entity) lines.push(`Encja: ${d.data.entity}`);
          if (d.data.stage) lines.push(`Etap: ${d.data.stage}`);
        }

        const rect = svgRef.current?.getBoundingClientRect();
        const cx = event.clientX - (rect?.left || 0);
        const cy = event.clientY - (rect?.top || 0);
        setTooltip({ x: cx, y: cy, lines });
      })
      .on("mousemove", function (event) {
        const rect = svgRef.current?.getBoundingClientRect();
        const cx = event.clientX - (rect?.left || 0);
        const cy = event.clientY - (rect?.top || 0);
        setTooltip((prev) => (prev ? { ...prev, x: cx, y: cy } : prev));
      })
      .on("mouseout", function () {
        d3.select(this).attr("stroke", "rgba(0,0,0,0.4)").attr("stroke-width", 0.5);
        path.attr("fill-opacity", (n) =>
          arcVisible(n.current!) ? (n.depth === 1 ? 0.85 : 0.92) : 0,
        );
        setTooltip(null);
      })
      .on("click", function (_event, d) {
        // Outer ring (node) → onNodeClick
        if (d.depth === 2 && d.data.nodeId) {
          onNodeClick?.(d.data.nodeId);
          return;
        }
        // Inner ring (seed) → zoom in
        if (d.depth === 1) {
          clicked(d as ArcNode);
        }
      });

    parent.on("click", function (_event, d) {
      if (d.depth === 0) {
        // already at root, nothing to do
        return;
      }
      clicked(d as ArcNode);
    });

    // Zoom transition
    function clicked(p: ArcNode) {
      // Update parent for zoom-out
      parent.datum(p.parent || hierarchy);

      // Compute target coords for all nodes
      allNodes.forEach((d) => {
        const rangeStart = (d.x0 - p.x0) / (p.x1 - p.x0);
        const rangeEnd = (d.x1 - p.x0) / (p.x1 - p.x0);
        d.target = {
          x0: Math.max(0, Math.min(1, rangeStart)) * 2 * Math.PI,
          x1: Math.max(0, Math.min(1, rangeEnd)) * 2 * Math.PI,
          y0: Math.max(0, d.y0 - p.depth),
          y1: Math.max(0, d.y1 - p.depth),
        };
      });

      const t = svg.transition().duration(620);

      path
        .transition(t as any)
        .tween("data", (d) => {
          const i = d3.interpolate(d.current, d.target);
          return (time) => {
            d.current = i(time);
          };
        })
        .filter(function (d) {
          return +(this as SVGPathElement).getAttribute("fill-opacity")! > 0 || arcVisible(d.target!);
        })
        .attr("fill-opacity", (d) =>
          arcVisible(d.target!) ? (d.depth === p.depth + 1 || d.depth === 1 ? 0.85 : 0.92) : 0,
        )
        .attrTween("d", (d) => () => arc(d) || "");

      label
        .filter(function (d) {
          return (
            +(this as SVGTextElement).getAttribute("fill-opacity")! > 0 ||
            labelVisible(d.target!, d.depth)
          );
        })
        .transition(t as any)
        .attr("fill-opacity", (d) => +labelVisible(d.target!, d.depth))
        .attrTween("transform", (d) => () => labelTransform(d.current!));

      // Update breadcrumbs
      const trail = p.ancestors().reverse().map((n) => n.data.name);
      setBreadcrumbs(trail);

      // Update center text
      const total = p.descendants().length - 1;
      countText.text(
        p.depth === 0
          ? `${seeds.length} seeds · ${nodes.length} nodes`
          : `${total} ${p.depth === 1 ? "nodes" : "elementów"}`,
      );
      hintText.text(p.depth === 0 ? "Klik w segment → zoom" : "Klik w środek → wróć");
    }

    // Initial breadcrumbs
    setBreadcrumbs([mapName]);
  }, [mapName, seeds, nodes, filteredNodeIds, onNodeClick, containerWidth, seedColorScale]);

  const resetZoom = () => {
    // Re-trigger effect by toggling a key — easiest: force re-render via state
    // Simple approach: programmatic click on parent (root)
    if (!svgRef.current) return;
    const evt = new Event("dblclick");
    svgRef.current.dispatchEvent(evt);
    setBreadcrumbs([mapName]);
  };

  return (
    <div className="space-y-3">
      <RoleLegend />

      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <div className="flex items-center gap-1 px-2 py-1 text-sm text-muted-foreground flex-wrap">
          {breadcrumbs.map((bc, i) => (
            <div key={`${bc}-${i}`} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-3 w-3 opacity-50" />}
              <Badge
                variant={i === breadcrumbs.length - 1 ? "default" : "outline"}
                className="text-xs"
              >
                {bc.length > 30 ? bc.slice(0, 27) + "…" : bc}
              </Badge>
            </div>
          ))}
          {breadcrumbs.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 ml-2 text-xs"
              onClick={resetZoom}
              title="Wróć do widoku ogólnego"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Reset
            </Button>
          )}
        </div>
      )}

      <div
        ref={containerRef}
        className="relative border rounded-lg bg-background/50 overflow-hidden"
        style={{ minHeight: 600 }}
      >
        <svg ref={svgRef} style={{ display: "block", margin: "0 auto" }} />
        {tooltip && (
          <div
            className="absolute bg-popover border rounded-lg px-3 py-2 text-xs shadow-xl pointer-events-none z-50 max-w-[280px] animate-in fade-in"
            style={{
              left: Math.min(tooltip.x + 12, (containerRef.current?.clientWidth || 1000) - 290),
              top: Math.max(tooltip.y - 10, 8),
            }}
          >
            {tooltip.lines.map((line, i) => (
              <div
                key={i}
                className={i === 0 ? "font-semibold mb-1" : "text-muted-foreground text-[11px]"}
              >
                {line}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
